﻿import { DataGrid, Decorators, Fluent, GridPersistanceFlags, LazyLoadHelper, PersistedGridSettings, Widget, htmlEncode } from "@serenity-is/corelib";
import { Column, FormatterResult, Grid } from "@serenity-is/sleekgrid";

export interface AutoColumnWidthMixinOptions {
    grid: DataGrid<any, any>;
    /** Disable auto size on load behavior, default is false, user can stil auto size by double click */
    disabled?: boolean;
    /** True to just auto size once on load, not on every load, like after changing pages, sorting etc, default is false */
    autoSizeAllJustOnce?: boolean;
    /** Disable auto size on load behavior if user manually resizes a column, default is true */
    disableIfUserResize?: boolean;
    /** If column has an explicit width set in Form.cs, use that width on auto size, doesn't effect manually triggered auto size */
    useExplicitWidths?: () => boolean;
    /** Set to true if headers shouldn't affect auto width, default is false.
     * If passed a number, header are considered for auto width but only up to that 
     * number, e.g. if you pass 200, and header width is 300, 200 will be used */
    ignoreHeaders?: boolean | number;
    /** Maximum rows to scan in data source, default is 40*/
    maxRows?: number;
    /** Maximum auto column width, default value is 400 */
    maxWidth?: number;
    /** Minimum auto column width, default value is 30 */
    minWidth?: number;
    /** Callback used before user auto size all columns */
    beforeUserAutoSizeAll?: () => void;
    /** Callback used after user auto size all columns */
    afterUserAutoSizeAll?: () => void;
    /** extra delta for input border and padding */
    inputDelta?: number;
}

/**
 * A mixin that can be applied to a DataGrid for auto size column width functionality
 */
@Decorators.registerClass("Serenity.AutoColumnWidthMixin")
export class AutoColumnWidthMixin extends Widget<AutoColumnWidthMixinOptions> {

    declare private slickGrid: Grid;
    declare private container: HTMLElement;
    private autoSizing: number = 0;
    declare private inputDelta: number;
    declare private markupReadyOnce: boolean;
    private oldMarkupReady: () => void;
    private oldPersistSettings: (flags?: GridPersistanceFlags) => void;
    private oldGetCurrentSettings: (flags?: GridPersistanceFlags) => PersistedGridSettings;
    private oldRestoreSettingsFrom: (settings?: PersistedGridSettings, flags?: GridPersistanceFlags) => void;
    declare public disabled: boolean;
    
    private columnResizeHandler = () => {
        if (!this.autoSizing && (this.options.disableIfUserResize ?? true))
            this.disabled = true;
    }

    constructor(props: AutoColumnWidthMixinOptions) {
        super({ element: props.grid.domNode, ...props });

        this.autoSizing = 0;
        let options = this.options;
        if (options.disabled != null)
            this.disabled = !!options.disabled;

        this.slickGrid = options.grid.slickGrid;
        this.container = (this.slickGrid as any).getContainerNode();

        Fluent.on(this.container, "dblclick." + this.uniqueName, '.slick-resizable-handle', (e: Event) => this.resizeHandleClick(e));
        this.slickGrid.onColumnsResized.subscribe(this.columnResizeHandler);
        var self = this;

        this.oldMarkupReady = options.grid['markupReady'];
        options.grid['markupReady'] = function () {
            self.oldMarkupReady && self.oldMarkupReady.call(this);
            if (!self.disabled && (!self.markupReadyOnce || !self.options.autoSizeAllJustOnce))
                self.autoSizeAll(self.disabled != null ? false : null);
            self.markupReadyOnce = true;
        };

        this.oldPersistSettings = options.grid['persistSettings'];
        options.grid['persistSettings'] = function (flags?: GridPersistanceFlags) {
            if (self.autoSizing)
                return;
            return self.oldPersistSettings.call(this, flags);
        };

        this.oldGetCurrentSettings = options.grid['getCurrentSettings'];
        options.grid['getCurrentSettings'] = function (flags?: GridPersistanceFlags): PersistedGridSettings {
            flags = flags || this.gridPersistanceFlags();
            flags.columnWidths = flags.columnWidths ?? !!self.disabled;
            return self.oldGetCurrentSettings.call(this, flags);
        }

        this.oldRestoreSettingsFrom = options.grid['restoreSettingsFrom'];
        options.grid['restoreSettingsFrom'] = function (settings: PersistedGridSettings, flags?: GridPersistanceFlags) {
            var myFlags = flags || this.gridPersistanceFlags();

            if (settings != null &&
                settings.columns &&
                (myFlags.columnWidths || settings.columns.some(x => x.width != null)))
                self.disabled = true;

            self.oldRestoreSettingsFrom.call(this, settings, flags);
        }

        this.inputDelta = options.inputDelta ?? 8;

        if (!this.disabled)
            this.autoSizeAll();
    }

    destroy() {
        if (this.options && this.options.grid) {
            Fluent.off(this.options.grid.domNode, '.' + this.uniqueName);
            this.options.grid['markupReady'] = this.oldMarkupReady;
        }

        this.slickGrid && this.slickGrid.onColumnsResized.unsubscribe(this.columnResizeHandler);

        delete this.options.grid;
        this.slickGrid = null;
        this.container = null;
        super.destroy();
    }

    private resizeHandleClick(e: Event) {
        if ((e as any).ctrlKey) {
            e.preventDefault();
            e.stopPropagation();
            this.options.beforeUserAutoSizeAll && this.options.beforeUserAutoSizeAll();
            this.autoSizeAll(false);
            this.options.afterUserAutoSizeAll && this.options.afterUserAutoSizeAll();
            this.disabled = false;
            return;
        }

        var header = (e.target as HTMLElement)?.closest?.('.slick-header-column') as HTMLElement;
        var columnDef = this.slickGrid?.getColumnFromNode(header);

        if (!columnDef || !columnDef.resizable)
            return;

        e.preventDefault();
        e.stopPropagation();

        this.autoSizeColumn(header, columnDef);
    }

    private createCellNodes(sb: string[]): HTMLDivElement[] {
        const row = document.createElement('div');
        row.classList.add('slick-row', 's-offscreen');
        row.style.visibility = 'hidden';

        row.innerHTML = sb.join('');
        if (row.childNodes.length > 0)
            this.slickGrid.getCanvasNode().appendChild(row);

        if (Array["from"])
            return Array["from"](row.childNodes) as HTMLDivElement[];

        var childNodes = row.childNodes;
        var length = row.childNodes.length;
        var cellNodes: HTMLDivElement[] = [];
        for (var i = 0; i < length; i++)
            cellNodes.push(childNodes[i] as HTMLDivElement);

        return cellNodes;
    }

    private calcMaxWidthFromCellNodes(cellNodes: HTMLDivElement[], columnDef: Column, header: HTMLElement) {
        var maxAllowedWidth = columnDef.maxWidth > 0 ? columnDef.maxWidth : this.options.maxWidth;
        var maxWidth = 0;

        for (let index = 0; index < cellNodes.length; index++) {
            const cellNode = cellNodes[index];

            var width = cellNode.offsetWidth +
                (cellNode.dataset?.isInputNode === "1" ? (this.inputDelta ?? 0) : 0);

            if (width > maxWidth)
                maxWidth = width;

            if (maxAllowedWidth > 0 && width >= maxAllowedWidth)
                break;
        }

        var autoWidth = maxWidth || 0;

        if (typeof this.options.ignoreHeaders == "number") {
            autoWidth = Math.max(Math.min(this.options.ignoreHeaders, header.offsetWidth, autoWidth));
        }
        else if (!this.options.ignoreHeaders) {
            autoWidth = Math.max(header.offsetWidth, autoWidth);
        }

        autoWidth = (autoWidth || 0) + 1;

        if (columnDef.maxWidth > 0)
            autoWidth = Math.min(autoWidth, columnDef.maxWidth);
        else
            autoWidth = Math.min(autoWidth, this.options.maxWidth || 400);

        if (columnDef.minWidth > 0)
            autoWidth = Math.max(autoWidth, columnDef.minWidth);
        else
            autoWidth = Math.max(autoWidth, this.options.minWidth || 30);

        return autoWidth;
    }

    private getAutoColumnWidth(header: HTMLElement, columnDef: Column) {
        var colIndex = this.slickGrid.getColumnIndex(columnDef.id);
        var sb: string[] = [];
        this.renderColumnCells(sb, columnDef, colIndex, this.getMaxCalcLength());
        const cellNodes = this.createCellNodes(sb);
        try {
            let headerClone: HTMLElement;
            if (!this.options.ignoreHeaders)
                headerClone = createHeaderClone(header);

            try {
                return this.calcMaxWidthFromCellNodes(cellNodes, columnDef, headerClone)
            } finally {
                headerClone?.parentElement?.removeChild(headerClone);
            }
        }
        finally {
            var rowNode = cellNodes[0]?.parentElement;
            rowNode && rowNode.parentElement.removeChild(rowNode);
        }
    }

    private autoSizeColumn(header: HTMLElement, columnDef: Column) {
        const autoWidth = this.getAutoColumnWidth(header, columnDef);

        if (autoWidth !== columnDef.width) {
            this.autoSizing++;
            try {
                columnDef.width = autoWidth;
                this.slickGrid.columnsResized(true);
            }
            finally {
                this.autoSizing--;
            }
        }
    }

    private queueAutoSize: number = 0;

    public autoSizeAll(useExplicitWidths?: boolean) {
        if (!this.container)
            return;

        if (!Fluent.isVisibleLike(this.container)) {

            if (this.queueAutoSize <= 0) {
                this.queueAutoSize++;
                LazyLoadHelper.executeOnceWhenShown(this.container, () => this.autoSizeAll(useExplicitWidths));
            }

            return;
        }

        if (this.queueAutoSize > 0) {
            this.queueAutoSize--;
        }

        this.autoSizing++;
        try {

            var headers = this.container.querySelectorAll<HTMLElement>(".slick-header-column");
            useExplicitWidths = useExplicitWidths ?? (this.options.useExplicitWidths && this.options.useExplicitWidths());
            var maxRows = this.getMaxCalcLength();
            let sb: string[] = [];
            const headerClones: HTMLElement[] = [];

            try {
                headers.forEach(header => {
                    var columnDef = this.slickGrid?.getColumnFromNode(header);
                    if (columnDef && this.getPreSetWidth(columnDef) == null) {
                        var colIndex = this.slickGrid.getColumnIndex(columnDef.id);
                        this.renderColumnCells(sb, columnDef, colIndex, maxRows);
                    }
                    else {
                        for (var i = 0; i < maxRows; i++)
                            sb.push('<div></div>');
                    }

                    if (!this.options.ignoreHeaders)
                        headerClones.push(createHeaderClone(header));
                });

                let allCellNodes = this.createCellNodes(sb);
                try {

                    headers.forEach((header, index) => {
                        var columnDef = this.slickGrid?.getColumnFromNode(header);
                        if (!columnDef)
                            return;

                        var cellNodes = allCellNodes.slice(index * maxRows, (index + 1) * maxRows);

                        var autoWidth = this.getPreSetWidth(columnDef);
                        if (autoWidth == null) {
                            autoWidth = this.calcMaxWidthFromCellNodes(cellNodes, columnDef, headerClones[index]);
                        }
                        columnDef.width = autoWidth;
                    });
                }
                finally {
                    var rowNode = allCellNodes[0]?.parentElement;
                    rowNode && rowNode.parentElement?.removeChild(rowNode);
                }
            } finally {
                headerClones.forEach(el => el?.parentElement?.removeChild(el));
            }

            this.slickGrid.columnsResized(true);
        }
        finally {
            this.autoSizing--;
        }
    }

    private getMaxCalcLength() {
        var length = this.slickGrid.getDataLength();
        if ((this.options.maxRows ?? 40) < length)
            length = this.options.maxRows ?? 40;
        return length;
    }

    private renderColumnCells(sb: string[], columnDef: Column, colIndex: number, maxRows: number) {

        for (var i = 0; i < maxRows; i++) {
            sb.push('<div class="slick-cell" style="text-overflow: initial !important; white-space: nowrap !important;"');

            var item = this.slickGrid.getDataItem(i);
            if (!item) {
                sb.push('></div>');
                continue;
            }

            const sanitizer = this.slickGrid.getOptions().sanitizer;
            var format = this.slickGrid.getFormatter(i, columnDef);
            var ctx = this.slickGrid.getFormatterContext(i, colIndex);
            let child: HTMLInputElement;
            let childHtml: FormatterResult;

            if (format) {
                try {
                    childHtml = format(ctx);
                    if (typeof childHtml === "string" && childHtml.length > 0 && sanitizer) {
                        childHtml = sanitizer(childHtml);
                    }
                }
                catch (ex) {
                    childHtml = htmlEncode(item[columnDef.field] ?? '');
                }
            }
            else
                childHtml = htmlEncode(item[columnDef.field]);

            if (childHtml instanceof Node) {
                childHtml = childHtml instanceof Element ? 
                    childHtml.outerHTML :
                    childHtml instanceof DocumentFragment ?
                    Array.from(childHtml.children).map(x => x.outerHTML).join("") : '';
            }

            if (childHtml?.length > 5 && childHtml.substring(0, 6) == "<input") {
                let cellTemplate = document.createElement('template');
                cellTemplate.innerHTML = childHtml;
                child = cellTemplate.content.firstChild as HTMLInputElement;

                if ((child as HTMLInputElement).type != "checkbox") {
                    sb.push(' data-isInputNode="1">', htmlEncode(child.value), '</div>');
                    continue;
                }
            }

            sb.push('>', childHtml, '</div>');
        }
    }

    getPreSetWidth(columnDef: Column): number {
        if (this.options.useExplicitWidths && columnDef.sourceItem && columnDef.sourceItem.width > 0 && columnDef.sourceItem.widthSet)
            return columnDef.sourceItem.width;
    }
}

function createHeaderClone(element: HTMLElement): HTMLElement {
    const clone = element.cloneNode(true) as HTMLElement;
    clone.style.cssText = 'position: absolute; visibility: hidden; right: auto; text-overflow: initial; white-space: nowrap;';
    element.parentNode.insertBefore(clone, element);

    return clone;
}